
import { Component, Vue } from "vue-property-decorator";
import { Navigation, PocketBook } from "@/hooks";
import AuthModule from "@/store/modules/Auth";
import dayjs from "dayjs";
interface GeolocationPosition {
  readonly coords: {
    readonly latitude: number;
    readonly longitude: number;
    readonly altitude: number | null;
    readonly accuracy: number;
    readonly altitudeAccuracy: number | null;
    readonly heading: number | null;
    readonly speed: number | null;
  };
  readonly timestamp: number;
}
interface link {
  color: string;
  icon: string;
  title: string;
  path?: string;
  action?: string;
}
@Component
export default class QuickLinks extends Vue {
  snackbar = false;
  snackbarText = "";
  snackbarColor = "success";
  loading = false;
  mounted() {
    this.$root.$on("show-snackbar", (data: { text: string; color: string }) => {
      this.snackbarText = data.text;
      this.snackbarColor = data.color;
      this.snackbar = true;
    });

    this.$root.$on("show-loading", (value: boolean) => {
      this.loading = value;
    });
  }
  hasAccess(name: string | undefined): boolean {
    if (name != "dash.manage.incidents") {
      const has = Navigation.hasAccess(name);
      // logger.log("Name: ", name, has);
      return has;
    } else {
      return AuthModule.getIncidentProxy;
    }
  }
  get getLinks(): link[] {
    return this.links.filter((e) => this.hasAccess(e.path) || e.action != null);
  }

  async goTo(name: string | undefined, action?: string): Promise<void> {
    if (action) {
      try {
        this.$root.$emit("show-loading", true);

        if (action === "dutyOn" || action === "dutyOff") {
          await this.handleDutyAction(action);
        }
      } catch (error) {
        console.error("Error processing action:", error);
        this.$root.$emit("show-snackbar", {
          text: `Failed to ${
            action === "dutyOn" ? "book on" : "book off"
          } duty. Please try again.`,
          color: "error",
        });
      } finally {
        this.$root.$emit("show-loading", false);
      }
    } else if (name != undefined) {
      this.$router.push({
        name: name,
      });
    }
  }
  async handleDutyAction(action: string): Promise<void> {
    try {
      // Get current position
      const position = await this.getCurrentPosition();
      const geoLocationStamp = `${position.coords.latitude},${position.coords.longitude}`;

      const currentTime = dayjs().format("YYYY-MM-DD HH:mm");

      // Prepare data for API call
      const data = {
        pocketBookId: 0,
        comment: action === "dutyOn" ? "Booked on duty" : "Booked off duty",
        title: action === "dutyOn" ? "Duty On" : "Duty Off",
        shiftStart: currentTime,
        shiftEnd: currentTime,
        obNumber: "000",
        geoLocationStamp: geoLocationStamp,
      };

      // Make API call
      await PocketBook.addPocketBookComments(data);

      // Show success message
      this.$root.$emit("show-snackbar", {
        text: `Successfully ${
          action === "dutyOn" ? "booked on" : "booked off"
        } duty`,
        color: "success",
      });
    } catch (error) {
      console.error("Error in handleDutyAction:", error);
      throw error;
    }
  }
  getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by this browser"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(position),
        (error) => reject(error),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  }
  links: link[] = [
    {
      color: "#1660A8",
      icon: "mdi-account-multiple",
      title: "Manage Administrators",
      path: "dash.manage.users",
    },
    {
      color: "#C1392B",
      icon: "mdi-account-multiple",
      title: "Manage Officers",
      path: "dash.manage.aro",
    },
    {
      color: "#1660A8",
      icon: "mdi-note-edit",
      title: "Manage Documents",
      path: "dash.manage.documents",
    },
    {
      color: "#103961",
      icon: "mdi-account-group",
      title: "Manage Teams",
      path: "dash.manage.teams",
    },
    {
      color: "#851C11",
      icon: "mdi-badge-account",
      title: "Manage Shifts",
      path: "dash.manage.shifts",
    },
    {
      color: "#C1392B",
      icon: "mdi-alert",
      title: "Manage Incidents",
      path: "dash.manage.incidents",
    },
    {
      color: "#103961",
      icon: "mdi-archive",
      title: "Permits",
      path: "dash.manage.permits",
    },
    {
      color: "#103961",
      icon: "mdi-check-decagram",
      title: "My Permits",
      path: "dash.myPermits",
    },
    {
      color: "#103961",
      icon: "mdi-check-decagram-outline",
      title: "Offline Permits",
      path: "dash.offlinePermits",
    },
    {
      color: "#232C35",
      icon: "mdi-target",
      title: "Request Firearm Permits",
    },
    // {
    //   color: "#979797",
    //   icon: "mdi-clipboard-arrow-left",
    //   title: "Return Firearm Permits",
    //   path: "dash.myPermits",
    // },
    {
      color: "#851C11",
      icon: "mdi-pistol",
      title: "Manage Firearms",
      path: "dash.registers.firearms",
    },
    {
      color: "#1660A8",
      icon: "mdi-bullet",
      title: "Manage Ammunition",
      path: "dash.registers.ammo",
    },
    {
      color: "#232C35",
      icon: "mdi-file-certificate",
      title: "Issue Permits",
    },
    {
      color: "#979797",
      icon: "mdi-calendar-clock",
      title: "Pending Permit Requests",
    },
    {
      color: "#103961",
      icon: "mdi-account-multiple",
      title: "Daily Combined",
      path: "dash.registers.dailyCombined",
    },
    {
      color: "#1660A8",
      icon: "mdi-book",
      title: "Guard Pocketbook",
      path: "dash.pocketbook.aro",
    },
    {
      color: "#1660A8",
      icon: "mdi-book",
      title: "Supervisor Pocketbook",
      path: "dash.pocketbook.supervisor",
    },
    {
      color: "#103961",
      icon: "mdi-book-plus",
      title: "Request Permit",
      path: "dash.requestPermit",
    },
    {
      color: "#2e7d32",
      icon: "mdi-clock-start",
      title: "Book On-Duty",
      action: "dutyOn",
    },
    {
      color: "#c62828",
      icon: "mdi-clock-end",
      title: "Book Off-Duty",
      action: "dutyOff",
    },
  ];
  beforeDestroy() {
    this.$root.$off("show-snackbar");
    this.$root.$off("show-loading");
  }
}
